import { useState, useEffect } from "react";
import { TrendingUp, TrendingDown, Calendar, Filter, RefreshCw } from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl() + "/api";

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


export default function GadsVsRac() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      
      const res = await authFetch(`${API}/gads-vs-rac?${params}`);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error("Error loading GAds vs RAC:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    loadData();
  };

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setTimeout(loadData, 100);
  };

  const fmt = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0);
  
  const fmtMonth = (m) => {
    if (!m) return "";
    const [year, month] = m.split("-");
    const months = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  // Generate month options for selects
  const generateMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 36; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      options.push({ value, label: fmtMonth(value) });
    }
    return options;
  };

  const monthOptions = generateMonthOptions();

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-400" />
        <span className="ml-2 text-slate-400">Chargement...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-red-400">
        <span className="mb-2">Erreur: {error}</span>
        <button 
          onClick={loadData}
          className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
        >
          Réessayer
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400">
        Aucune donnée disponible
      </div>
    );
  }

  const isPositive = data?.total_difference >= 0;

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            Google Ads vs RAC Encaissé
          </h3>
          <p className="text-sm text-slate-400">Comparaison des dépenses publicitaires et des encaissements RAC</p>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 bg-slate-800/50 rounded-lg px-3 py-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <select
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-transparent text-sm border-none focus:ring-0 text-white"
            >
              <option value="">Début</option>
              {monthOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <span className="text-slate-500">→</span>
            <select
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-transparent text-sm border-none focus:ring-0 text-white"
            >
              <option value="">Fin</option>
              {monthOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={applyFilters}
            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors"
          >
            <Filter className="w-4 h-4" />
          </button>
          {(startDate || endDate) && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors"
            >
              Effacer
            </button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-1">Dépenses Google Ads</p>
          <p className="text-xl font-bold text-amber-400">{fmt(data?.total_gads)}</p>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-1">RAC Encaissé</p>
          <p className="text-xl font-bold text-emerald-400">{fmt(data?.total_rac)}</p>
        </div>
        <div className={`${isPositive ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'} border rounded-xl p-4`}>
          <p className="text-xs text-slate-400 mb-1">Différence (RAC - GAds)</p>
          <p className={`text-xl font-bold flex items-center gap-1 ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
            {isPositive ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
            {fmt(data?.total_difference)}
          </p>
        </div>
        <div className={`${data?.global_ratio >= 100 ? 'bg-purple-500/10 border-purple-500/30' : 'bg-orange-500/10 border-orange-500/30'} border rounded-xl p-4`}>
          <p className="text-xs text-slate-400 mb-1">Ratio RAC/GAds</p>
          <p className={`text-xl font-bold ${data?.global_ratio >= 100 ? 'text-purple-400' : 'text-orange-400'}`}>
            {data?.global_ratio?.toFixed(1)}%
          </p>
        </div>
      </div>

      {/* Monthly Table */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-700/50 flex justify-between items-center">
          <h4 className="font-semibold">Évolution mensuelle</h4>
          <span className="text-sm text-slate-400">{data?.months_count || 0} mois</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-400 border-b border-slate-700/50 bg-slate-900/30">
                <th className="text-left py-3 px-4 font-medium">Mois</th>
                <th className="text-right py-3 px-4 font-medium">Dépenses GAds</th>
                <th className="text-right py-3 px-4 font-medium">RAC Encaissé</th>
                <th className="text-right py-3 px-4 font-medium">Nb Encaissements</th>
                <th className="text-right py-3 px-4 font-medium">Différence</th>
                <th className="text-right py-3 px-4 font-medium">Ratio</th>
                <th className="py-3 px-4 font-medium w-48">Visualisation</th>
              </tr>
            </thead>
            <tbody>
              {data?.monthly_data?.map((row, idx) => {
                const rowPositive = row.difference >= 0;
                const maxVal = Math.max(row.gads, row.rac) || 1;
                const gadsWidth = (row.gads / maxVal) * 100;
                const racWidth = (row.rac / maxVal) * 100;
                
                return (
                  <tr key={row.month} className={`border-b border-slate-700/30 ${idx % 2 === 0 ? 'bg-slate-900/20' : ''}`}>
                    <td className="py-3 px-4 font-medium">{fmtMonth(row.month)}</td>
                    <td className="py-3 px-4 text-right text-amber-400">{fmt(row.gads)}</td>
                    <td className="py-3 px-4 text-right text-emerald-400">{fmt(row.rac)}</td>
                    <td className="py-3 px-4 text-right text-slate-300">{row.rac_count}</td>
                    <td className={`py-3 px-4 text-right font-medium ${rowPositive ? 'text-green-400' : 'text-red-400'}`}>
                      {rowPositive ? '+' : ''}{fmt(row.difference)}
                    </td>
                    <td className={`py-3 px-4 text-right font-medium ${row.ratio >= 100 ? 'text-purple-400' : 'text-orange-400'}`}>
                      {row.ratio?.toFixed(1)}%
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-1">
                        <div className="h-3 bg-slate-700/30 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-amber-500 rounded-full transition-all"
                            style={{ width: `${gadsWidth}%` }}
                            title={`GAds: ${fmt(row.gads)}`}
                          />
                        </div>
                        <div className="h-3 bg-slate-700/30 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-emerald-500 rounded-full transition-all"
                            style={{ width: `${racWidth}%` }}
                            title={`RAC: ${fmt(row.rac)}`}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Legend */}
        <div className="px-4 py-3 border-t border-slate-700/50 flex items-center gap-6 text-xs text-slate-400">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-amber-500 rounded-full" />
            <span>Dépenses Google Ads</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-emerald-500 rounded-full" />
            <span>RAC Encaissé</span>
          </div>
        </div>
      </div>
    </div>
  );
}
